import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import { NetworkAvailableFeature } from '../../auth/networkFeatures';
import { assignableShigoRoles } from '../../auth/permissions';
import {
  NetworkCostType,
  NetworkCpmMetric,
  NetworkRegion,
  NetworkStatus,
  NetworkTcfEnforcement,
  type Network,
  type NetworkMutationPayload,
  type NetworkSettingsPayload,
} from './model';

const optionalCostType = z.union([z.enum(NetworkCostType), z.literal('')]);
const optionalCpmMetric = z.union([z.enum(NetworkCpmMetric), z.literal('')]);
const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, dashboardT('networks.validation.hexColor'));
const supportedLogoTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const maxLogoSize = 2 * 1024 * 1024;
const optionalLogoFile = z
  .custom<File | null>(
    (value) => value === null || (typeof File !== 'undefined' && value instanceof File),
    dashboardT('networks.validation.logoType'),
  )
  .superRefine((file, context) => {
    if (!file) return;
    if (!supportedLogoTypes.includes(file.type)) {
      context.addIssue({ code: 'custom', message: dashboardT('networks.validation.logoType') });
    }
    if (file.size > maxLogoSize) {
      context.addIssue({ code: 'custom', message: dashboardT('networks.validation.logoSize') });
    }
  });

const brandDetailsSchema = z
  .object({
    logoUrl: z.string(),
    logoFile: optionalLogoFile,
    logoLightUrl: z.string(),
    logoLightFile: optionalLogoFile,
    accent: hexColor,
    accentStrong: hexColor,
    sidebar: z.union([z.literal(''), hexColor]),
  })
  .superRefine((value, context) => {
    if (!value.logoUrl.trim() && !value.logoFile) addRequiredIssue(context, ['logoFile']);
    if (!value.logoLightUrl.trim() && !value.logoLightFile) {
      addRequiredIssue(context, ['logoLightFile']);
    }
  });

const regionSchema = z.object({
  id: z.string(),
  region: z.enum(NetworkRegion),
  platformName: z.string(),
  rootDomain: z.string(),
  exchangeBidderName: z.string(),
  backendDomain: z.string(),
  exchangeDomain: z.string(),
  trackersDomain: z.string(),
  cdn: z.string(),
  a2v: z.string(),
  tagId: z.string(),
  logoUrl: z.string(),
  logoLightUrl: z.string(),
  faviconUrl: z.string(),
  supportEmail: z.string(),
  s3bucketDo: z.string(),
  cdnDo: z.string(),
  rosConfig: z.string(),
});

export const networkFormSchema = z
  .object({
    name: z.string().trim().min(1, dashboardT('networks.validation.required')),
    qps: z.string(),
    saasCostType: optionalCostType,
    saasCpmMetric: optionalCpmMetric,
    saasCpmValue: z.string(),
    saasRevenueShare: z.string(),
    dashboardDomain: z.string(),
    adstxtDomain: z.string(),
    tcfEnforcementEnabled: z.enum(NetworkTcfEnforcement),
    skipDefaultNetworkNode: z.boolean(),
    features: z.array(z.enum(NetworkAvailableFeature)),
    allowedRoles: z.array(z.enum(assignableShigoRoles)),
    brandDetails: brandDetailsSchema,
    us: regionSchema,
    eu: regionSchema,
  })
  .superRefine((value, context) => {
    validateOptionalNonnegative(value.qps, ['qps'], context);
    if (value.saasCostType === NetworkCostType.Cpm) {
      if (!value.saasCpmMetric) addRequiredIssue(context, ['saasCpmMetric']);
      validateRequiredNonnegative(value.saasCpmValue, ['saasCpmValue'], context);
    }
    if (value.saasCostType === NetworkCostType.RevenueShare) {
      const percentage = Number(value.saasRevenueShare);
      if (
        !value.saasRevenueShare.trim() ||
        !Number.isFinite(percentage) ||
        percentage < 0 ||
        percentage > 100
      ) {
        context.addIssue({
          code: 'custom',
          path: ['saasRevenueShare'],
          message: dashboardT('networks.validation.percentage'),
        });
      }
    }
    const usValidation = validateRegion(value.us, ['us'], context);
    const euValidation = validateRegion(value.eu, ['eu'], context);
    if (!usValidation.attempted && !euValidation.attempted) {
      context.addIssue({
        code: 'custom',
        path: ['us', 'platformName'],
        message: dashboardT('networks.validation.regionRequired'),
      });
    }
  });

// Logo uploads are optional on update, including networks without persisted logos.
export const networkEditorFormSchema = networkFormSchema.safeExtend({
  brandDetails: z
    .object({
      ...brandDetailsSchema.shape,
      accent: z.union([z.literal(''), hexColor]),
      accentStrong: z.union([z.literal(''), hexColor]),
    })
    .superRefine((value, context) => {
      const hasBrand = Boolean(
        value.accent ||
        value.accentStrong ||
        value.sidebar ||
        value.logoFile ||
        value.logoLightFile,
      );
      if (!hasBrand) return;
      if (!value.accent) addRequiredIssue(context, ['accent']);
      if (!value.accentStrong) addRequiredIssue(context, ['accentStrong']);
    }),
});

export const networkCreateFormSchema = networkFormSchema.superRefine((value, context) => {
  if (!value.brandDetails.logoFile) addRequiredIssue(context, ['brandDetails', 'logoFile']);
  if (!value.brandDetails.logoLightFile)
    addRequiredIssue(context, ['brandDetails', 'logoLightFile']);
});

export type NetworkFormValue = z.infer<typeof networkFormSchema>;

export function emptyNetworkFormValue(): NetworkFormValue {
  return {
    name: '',
    qps: '',
    saasCostType: NetworkCostType.RevenueShare,
    saasCpmMetric: '',
    saasCpmValue: '',
    saasRevenueShare: '',
    dashboardDomain: '',
    adstxtDomain: '',
    tcfEnforcementEnabled: NetworkTcfEnforcement.Off,
    skipDefaultNetworkNode: false,
    features: [],
    allowedRoles: [],
    brandDetails: {
      logoUrl: '',
      logoFile: null,
      logoLightUrl: '',
      logoLightFile: null,
      accent: '#1A88E0',
      accentStrong: '#1470C0',
      sidebar: '',
    },
    us: emptyRegion(NetworkRegion.Us),
    eu: emptyRegion(NetworkRegion.Eu),
  };
}

export function networkToFormValue(network: Network): NetworkFormValue {
  const us = network.networkSettings.find((settings) => settings.region === NetworkRegion.Us);
  const eu = network.networkSettings.find((settings) => settings.region === NetworkRegion.Eu);
  return {
    ...emptyNetworkFormValue(),
    name: network.name,
    qps: numberString(network.qps),
    saasCostType: network.saasCost?.costType ?? '',
    saasCpmMetric: network.saasCost?.cpmMetric ?? '',
    saasCpmValue:
      network.saasCost?.costType === NetworkCostType.Cpm
        ? numberString(network.saasCost.value)
        : '',
    saasRevenueShare:
      network.saasCost?.costType === NetworkCostType.RevenueShare
        ? numberString(network.saasCost.value === null ? null : network.saasCost.value * 100)
        : '',
    dashboardDomain: network.dashboardDomain ?? '',
    adstxtDomain: network.adstxtDomain ?? '',
    tcfEnforcementEnabled: network.tcfEnforcementEnabled ?? NetworkTcfEnforcement.Off,
    skipDefaultNetworkNode: network.skipDefaultNetworkNode,
    features: network.features,
    allowedRoles: network.allowedRoles,
    brandDetails: network.brandDetails
      ? {
          logoUrl: network.brandDetails.logoUrl ?? '',
          logoFile: null,
          logoLightUrl: network.brandDetails.logoLightUrl ?? '',
          logoLightFile: null,
          accent: network.brandDetails.accent,
          accentStrong: network.brandDetails.accentStrong,
          sidebar: network.brandDetails.sidebar ?? '',
        }
      : { ...emptyNetworkFormValue().brandDetails, accent: '', accentStrong: '' },
    us: settingsToFormValue(us, NetworkRegion.Us),
    eu: settingsToFormValue(eu, NetworkRegion.Eu),
  };
}

export function serializeNetworkFormValue(
  value: NetworkFormValue,
  editing: boolean,
): NetworkMutationPayload {
  const settings = [value.us, value.eu].filter(regionHasRequiredFields).map(serializeRegion);
  const saasCost = value.saasCostType
    ? {
        costType: value.saasCostType,
        cpmMetric:
          value.saasCostType === NetworkCostType.Cpm && value.saasCpmMetric
            ? value.saasCpmMetric
            : null,
        value:
          value.saasCostType === NetworkCostType.Cpm
            ? Number(value.saasCpmValue)
            : Number(value.saasRevenueShare) / 100,
      }
    : null;
  return {
    name: value.name.trim(),
    qps: value.qps.trim() ? Number(value.qps) : null,
    ...(!editing ? { status: NetworkStatus.Active } : {}),
    saasCost,
    dashboardDomain: value.dashboardDomain.trim(),
    adstxtDomain: value.adstxtDomain.trim(),
    tcfEnforcementEnabled: value.tcfEnforcementEnabled,
    skipDefaultNetworkNode: value.skipDefaultNetworkNode,
    features: value.features,
    allowedRoles: value.allowedRoles,
    // BE explicitly treats an omitted update palette as preserve, not reset.
    ...(!editing || value.brandDetails.accent || value.brandDetails.accentStrong
      ? {
          brandDetails: {
            accent: value.brandDetails.accent.toUpperCase(),
            accentStrong: value.brandDetails.accentStrong.toUpperCase(),
            sidebar: nullableTrimmed(value.brandDetails.sidebar)?.toUpperCase() ?? null,
          },
        }
      : {}),
    ...(editing ? { networkSettingsUpdateRequests: settings } : { networkSettings: settings }),
  };
}

function emptyRegion(region: NetworkRegion): NetworkFormValue['us'] {
  return {
    id: '',
    region,
    platformName: '',
    rootDomain: '',
    exchangeBidderName: '',
    backendDomain: '',
    exchangeDomain: '',
    trackersDomain: '',
    cdn: '',
    a2v: '',
    tagId: '',
    logoUrl: '',
    logoLightUrl: '',
    faviconUrl: '',
    supportEmail: '',
    s3bucketDo: '',
    cdnDo: '',
    rosConfig: '',
  };
}

function settingsToFormValue(
  settings: Network['networkSettings'][number] | undefined,
  region: NetworkRegion,
) {
  return {
    ...emptyRegion(region),
    ...(settings
      ? {
          id: settings.id ?? '',
          platformName: settings.platformName,
          rootDomain: settings.rootDomain,
          exchangeBidderName: settings.exchangeBidderName,
          backendDomain: settings.backendDomain ?? '',
          exchangeDomain: settings.exchangeDomain ?? '',
          trackersDomain: settings.trackersDomain ?? '',
          cdn: settings.cdn ?? '',
          a2v: settings.a2v ?? '',
          tagId: numberString(settings.tagId),
          logoUrl: settings.logoUrl ?? '',
          logoLightUrl: settings.logoLightUrl ?? '',
          faviconUrl: settings.faviconUrl ?? '',
          supportEmail: settings.supportEmail ?? '',
          s3bucketDo: settings.s3bucketDo ?? '',
          cdnDo: settings.cdnDo ?? '',
          rosConfig: settings.rosConfig ?? '',
        }
      : {}),
  };
}

function serializeRegion(region: NetworkFormValue['us']): NetworkSettingsPayload {
  return {
    ...(region.id ? { id: region.id } : {}),
    region: region.region,
    platformName: region.platformName.trim(),
    rootDomain: region.rootDomain.trim(),
    exchangeBidderName: region.exchangeBidderName.trim(),
    backendDomain: region.backendDomain.trim(),
    exchangeDomain: region.exchangeDomain.trim(),
    trackersDomain: region.trackersDomain.trim(),
    cdn: region.cdn.trim(),
    a2v: region.a2v.trim(),
    tagId: region.tagId.trim() ? Number(region.tagId) : null,
    logoUrl: region.logoUrl.trim(),
    logoLightUrl: region.logoLightUrl.trim(),
    faviconUrl: region.faviconUrl.trim(),
    supportEmail: region.supportEmail.trim(),
    s3bucketDo: region.s3bucketDo.trim(),
    cdnDo: region.cdnDo.trim(),
    rosConfig: region.rosConfig.trim(),
  };
}

function regionHasRequiredFields(region: NetworkFormValue['us']) {
  return Boolean(
    region.platformName.trim() && region.rootDomain.trim() && region.exchangeBidderName.trim(),
  );
}

function validateRegion(
  region: NetworkFormValue['us'],
  path: 'us'[] | 'eu'[],
  context: z.RefinementCtx,
) {
  const values = [region.platformName, region.rootDomain, region.exchangeBidderName];
  const attempted = values.some((field) => field.trim());
  if (!attempted) return { attempted: false, complete: false };
  (['platformName', 'rootDomain', 'exchangeBidderName'] as const).forEach((field) => {
    if (!region[field].trim()) addRequiredIssue(context, [...path, field]);
  });
  validateOptionalNonnegative(region.tagId, [...path, 'tagId'], context);
  return { attempted: true, complete: values.every((field) => field.trim()) };
}

function validateOptionalNonnegative(
  value: string,
  path: (string | number)[],
  context: z.RefinementCtx,
) {
  if (!value.trim()) return;
  validateRequiredNonnegative(value, path, context);
}

function validateRequiredNonnegative(
  value: string,
  path: (string | number)[],
  context: z.RefinementCtx,
) {
  const number = Number(value);
  if (!value.trim() || !Number.isFinite(number) || number < 0) {
    context.addIssue({
      code: 'custom',
      path,
      message: dashboardT('networks.validation.nonnegative'),
    });
  }
}

function addRequiredIssue(context: z.RefinementCtx, path: (string | number)[]) {
  context.addIssue({
    code: 'custom',
    path,
    message: dashboardT('networks.validation.required'),
  });
}

function numberString(value: number | null | undefined) {
  return value === null || value === undefined ? '' : String(value);
}

function nullableTrimmed(value: string) {
  const trimmed = value.trim();
  return trimmed || null;
}
